/**
 * DonationsPublicPage — Fundraising Suite (epic TRUS-326), Track A public checkout.
 *
 * Unauthenticated donor-facing donation page reached at /donate/{cboId}. Mirrors
 * the SmsOptInPage public pattern: it self-fetches org meta and posts to the
 * public donation endpoints (no auth / no X-CBO-ID). Payment is handled by a
 * Stripe-hosted Checkout page (the backend returns a redirect URL), so no Stripe
 * SDK is bundled here and no card data touches our UI. On success Stripe returns
 * the donor to this page with ?status=success.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { CheckCircle, Heart, Loader2 } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

interface DonateMeta {
  cbo_id: number;
  organization_name: string;
  logo_url?: string | null;
  currency?: string | null;
}

const PRESETS = [25, 50, 100, 250];

export default function DonationsPublicPage({ cboId }: { cboId: string }) {
  const [meta, setMeta] = useState<DonateMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [amount, setAmount] = useState<string>('50');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const succeeded = new URLSearchParams(window.location.search).get('status') === 'success';

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(getWeaveinApiUrl(`/api/donations/public/${encodeURIComponent(cboId)}/meta`));
        if (!active) return;
        if (res.ok) {
          setMeta(await res.json());
        } else {
          setNotFound(true);
        }
      } catch {
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [cboId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setError('Please enter a donation amount.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const origin = window.location.origin;
      const returnBase = `${origin}/donate/${encodeURIComponent(cboId)}`;
      const res = await fetch(getWeaveinApiUrl(`/api/donations/public/${encodeURIComponent(cboId)}/checkout`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amt,
          donor_name: name.trim() || undefined,
          donor_email: email.trim() || undefined,
          success_url: `${returnBase}?status=success`,
          cancel_url: `${returnBase}?status=cancelled`,
        }),
      });
      if (!res.ok) throw new Error('Could not start checkout');
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else if (data.mock) {
        // Dev/mock mode (Stripe disabled): no hosted page — show a thank-you.
        window.location.href = `${returnBase}?status=success`;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (notFound || !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center text-gray-600">
            This donation page is not available.
          </CardContent>
        </Card>
      </div>
    );
  }

  const orgName = meta.organization_name || 'this organization';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 flex items-center justify-center">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          {meta.logo_url ? (
            <img src={meta.logo_url} alt={orgName} className="mx-auto mb-2 h-12 object-contain" />
          ) : (
            <Heart className="mx-auto mb-2 h-10 w-10 text-blue-600" />
          )}
          <h1 className="text-xl font-semibold text-gray-900">Donate to {orgName}</h1>
        </CardHeader>
        <CardContent>
          {succeeded ? (
            <div className="py-8 text-center">
              <CheckCircle className="mx-auto mb-3 h-12 w-12 text-green-500" />
              <p className="text-lg font-medium text-gray-900">Thank you for your gift!</p>
              <p className="mt-1 text-sm text-gray-500">
                A receipt will be emailed to you. Your support makes a difference.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Amount</label>
                <div className="mb-2 grid grid-cols-4 gap-2">
                  {PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setAmount(String(p))}
                      className={`rounded border px-2 py-2 text-sm font-medium ${
                        amount === String(p)
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      ${p}
                    </button>
                  ))}
                </div>
                <Input
                  type="number"
                  min="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Other amount"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email (for your receipt)</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              {error ? <p className="text-sm text-red-600">{error}</p> : null}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…
                  </>
                ) : (
                  `Donate $${amount || '0'}`
                )}
              </Button>
              <p className="text-center text-xs text-gray-400">Secure payment processed by Stripe.</p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
